import { useState, useEffect, useCallback, useRef } from "react";
import { useDialogFocus } from "../../../../hooks/useDialogFocus";
import { DateTimePicker } from "../../../../components/DateTimePicker";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import "./CallModal.css";

interface RescheduleCallModalProps {
  isOpen: boolean;
  prospectName: string;
  /** The call's current scheduledAt (ISO) used to seed the input. */
  currentScheduledAt: string | null;
  saving?: boolean;
  onClose: () => void;
  /** Called with the new ISO instant when the user confirms. */
  onConfirm: (scheduledAtIso: string) => void;
}

/**
 * Compact modal for changing a call's scheduled date/time. Shares CallModal.css
 * with the other call dialogs so the overlay and footer match.
 */
export function RescheduleCallModal({
  isOpen,
  prospectName,
  currentScheduledAt,
  saving = false,
  onClose,
  onConfirm,
}: RescheduleCallModalProps) {
  const [scheduledAtIso, setScheduledAtIso] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setScheduledAtIso(currentScheduledAt);
    }
  }, [isOpen, currentScheduledAt]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !saving) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      return () => document.removeEventListener("keydown", handleEsc);
    }
  }, [isOpen, saving, onClose]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget && !saving) {
        onClose();
      }
    },
    [onClose, saving]
  );

  const canSave = !!scheduledAtIso && !saving;

  const handleConfirm = () => {
    if (scheduledAtIso) onConfirm(scheduledAtIso);
  };

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogFocus({ isOpen, dialogRef, handleEscape: false });

  if (!isOpen) return null;

  return (
    <div className="schedule-call-modal__overlay" onClick={handleOverlayClick}>
      <div ref={dialogRef} tabIndex={-1}
        className="schedule-call-modal__content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reschedule-call-modal-title"
      >
        <div className="schedule-call-modal__header">
          <div className="schedule-call-modal__header-content">
            <h3
              id="reschedule-call-modal-title"
              className="schedule-call-modal__title"
            >
              Reschedule Call
            </h3>
            <p className="schedule-call-modal__subtitle">{prospectName}</p>
          </div>
          <ModalCloseButton onClick={onClose} disabled={saving} />
        </div>

        <div className="schedule-call-modal__body">
          <div className="schedule-call-modal__field">
            <DateTimePicker
              id="reschedule-call-datetime"
              label="New Date & Time"
              value={scheduledAtIso}
              onChange={setScheduledAtIso}
              testId="reschedule-call-datetime"
            />
          </div>
        </div>

        <div className="schedule-call-modal__footer">
          <button
            type="button"
            className="schedule-call-modal__cancel-button"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="schedule-call-modal__save-button"
            data-testid="reschedule-call-submit"
            onClick={handleConfirm}
            disabled={!canSave}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
